import {
  CheckCircle,
  TrendingUp,
  Eye,
  Mail,
  MessageSquare,
  ChevronRight,
} from 'lucide-react';
import { EducationalContent } from './educationTypes';

interface Props {
  content: EducationalContent[];
}

export default function CRMEngagementTab({ content }: Props) {
  return (
    <div className="space-y-6">
      {/* Engagement Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Total Sent</h3>
            <Mail className="w-6 h-6 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">1,234</p>
          <p className="text-sm text-gray-600">Via email reminders</p>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>+12% from last month</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Open Rate</h3>
            <Eye className="w-6 h-6 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">68%</p>
          <p className="text-sm text-gray-600">Patients opened content</p>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>+5% from last month</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Completion Rate</h3>
            <CheckCircle className="w-6 h-6 text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">45%</p>
          <p className="text-sm text-gray-600">Fully engaged patients</p>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>+8% from last month</span>
          </div>
        </div>
      </div>

      {/* Top Performing Content */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <TrendingUp className="w-6 h-6 mr-2 text-green-600" />
          Top Performing Educational Content
        </h2>

        <div className="space-y-4">
          {[...content]
            .sort((a, b) => b.engagementRate - a.engagementRate)
            .map((item, index) => (
              <div key={item.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  #{index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 truncate">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.category}</p>
                </div>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="text-center">
                    <p className="font-bold text-gray-900">{item.viewCount}</p>
                    <p className="text-gray-500">Views</p>
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-green-600">{item.engagementRate}%</p>
                    <p className="text-gray-500">Engagement</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            ))}
        </div>
      </div>

      {/* Email Reminder Integration */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <MessageSquare className="w-6 h-6 mr-2 text-green-600" />
          Email Reminder Integration
        </h2>
        <p className="text-gray-600 mb-4">
          Educational content is automatically sent via email reminders. Track delivery and engagement metrics in the Reminders module.
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-semibold text-green-900">Integration Active</p>
              <p className="text-sm text-green-700 mt-1">
                All patient education assignments are synced with the reminder system and sent via email to patient inboxes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
